import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checklists = () => {
  const { t } = useTranslation();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('daily');
  
  const [newChecklist, setNewChecklist] = useState({
    title: '',
    checklist_type: 'daily',
    items: []
  });
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemRecurring, setNewItemRecurring] = useState(false);

  useEffect(() => {
    fetchChecklists();
  }, []);

  const fetchChecklists = async () => {
    try {
      const response = await axios.get(`${API}/checklists`);
      setChecklists(response.data);
    } catch (error) {
      console.error('Failed to fetch checklists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChecklist = async () => {
    if (!newChecklist.title.trim()) {
      toast.error('Bitte Titel eingeben');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/checklists`, newChecklist);
      setChecklists([response.data, ...checklists]);
      setNewChecklist({ title: '', checklist_type: 'daily', items: [] });
      setDialogOpen(false);
      toast.success(t('checklistCreated'));
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = () => {
    if (!newItemTitle.trim()) return;
    
    setNewChecklist({
      ...newChecklist,
      items: [...newChecklist.items, { title: newItemTitle, is_recurring: newItemRecurring }]
    });
    setNewItemTitle('');
    setNewItemRecurring(false);
  };

  const handleRemoveItem = (index) => {
    setNewChecklist({
      ...newChecklist,
      items: newChecklist.items.filter((_, i) => i !== index)
    });
  };

  const handleToggleItem = async (checklistId, itemId, completed) => {
    try {
      const response = await axios.put(`${API}/checklists/${checklistId}/items`, {
        item_id: itemId,
        completed
      });
      setChecklists(checklists.map(c => c.id === checklistId ? response.data : c));
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleAddItemToChecklist = async (checklistId, title) => {
    if (!title.trim()) return;
    
    try {
      const response = await axios.post(`${API}/checklists/${checklistId}/items`, {
        title,
        is_recurring: false
      });
      setChecklists(checklists.map(c => c.id === checklistId ? response.data : c));
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleDeleteItem = async (checklistId, itemId) => {
    try {
      await axios.delete(`${API}/checklists/${checklistId}/items/${itemId}`);
      setChecklists(checklists.map(c => {
        if (c.id === checklistId) {
          return {
            ...c,
            items: c.items.filter(i => i.id !== itemId),
            progress: c.items.filter(i => i.id !== itemId).length > 0
              ? (c.items.filter(i => i.id !== itemId && i.completed).length / c.items.filter(i => i.id !== itemId).length) * 100
              : 0
          };
        }
        return c;
      }));
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleDeleteChecklist = async (checklistId) => {
    try {
      await axios.delete(`${API}/checklists/${checklistId}`);
      setChecklists(checklists.filter(c => c.id !== checklistId));
      toast.success('Checkliste gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const dailyChecklists = checklists.filter(c => c.checklist_type === 'daily');
  const monthlyChecklists = checklists.filter(c => c.checklist_type === 'monthly');

  const ChecklistCard = ({ checklist }) => {
    const [newItem, setNewItem] = useState('');
    
    return (
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid={`checklist-${checklist.id}`}>
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-base font-semibold text-slate-900 font-[Manrope]">
                {checklist.title}
              </CardTitle>
              <div className="flex items-center gap-2 mt-2">
                <Progress value={checklist.progress} className="h-2 flex-1" />
                <span className="text-sm font-medium text-slate-600">
                  {Math.round(checklist.progress)}%
                </span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-slate-400 hover:text-red-500"
              onClick={() => handleDeleteChecklist(checklist.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {checklist.items.map((item) => (
            <div 
              key={item.id}
              className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-slate-50 transition-colors group"
            >
              <Checkbox
                checked={item.completed}
                onCheckedChange={(checked) => handleToggleItem(checklist.id, item.id, checked)}
              />
              <span className={`flex-1 text-sm ${item.completed ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                {item.title}
              </span>
              {item.is_recurring && (
                <RefreshCw className="h-3 w-3 text-slate-400" />
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
                onClick={() => handleDeleteItem(checklist.id, item.id)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
          
          {/* Add new item */}
          <div className="flex items-center gap-2 pt-2">
            <Input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder="Neues Element..."
              className="h-8 text-sm"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddItemToChecklist(checklist.id, newItem);
                  setNewItem('');
                }
              }}
            />
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-[#D92632] hover:text-[#B91C26] hover:bg-red-50"
              onClick={() => {
                handleAddItemToChecklist(checklist.id, newItem);
                setNewItem('');
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="checklists-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 font-[Manrope]">{t('checklists')}</h2>
          <p className="text-slate-500 mt-1 text-sm">{checklists.length} Checklisten</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#D92632] hover:bg-[#B91C26] text-white h-9 sm:h-10 text-sm"
              data-testid="new-checklist-btn"
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('newChecklist')}</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto mx-4">
            <DialogHeader>
              <DialogTitle className="font-[Manrope]">{t('newChecklist')}</DialogTitle>
              <DialogDescription>Erstellen Sie eine neue Checkliste</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>{t('checklistTitle')}</Label>
                <Input
                  value={newChecklist.title}
                  onChange={(e) => setNewChecklist({ ...newChecklist, title: e.target.value })}
                  placeholder="z.B. Morgenroutine"
                  className="mt-1"
                  data-testid="checklist-title-input"
                />
              </div>
              
              <div>
                <Label>Typ</Label>
                <Select 
                  value={newChecklist.checklist_type} 
                  onValueChange={(v) => setNewChecklist({ ...newChecklist, checklist_type: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">{t('daily')}</SelectItem>
                    <SelectItem value="monthly">{t('monthly')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Elemente</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={newItemTitle}
                    onChange={(e) => setNewItemTitle(e.target.value)}
                    placeholder="Element hinzufügen..."
                    onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleAddItem}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox
                    id="recurring"
                    checked={newItemRecurring}
                    onCheckedChange={setNewItemRecurring}
                  />
                  <Label htmlFor="recurring" className="text-sm font-normal">
                    {t('recurring')}
                  </Label>
                </div>
                
                {newChecklist.items.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {newChecklist.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <span className="flex-1 text-sm">{item.title}</span>
                        {item.is_recurring && <RefreshCw className="h-3 w-3 text-slate-400" />}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-slate-400 hover:text-red-500"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button 
                  onClick={handleCreateChecklist}
                  className="bg-[#D92632] hover:bg-[#B91C26]"
                  disabled={saving}
                  data-testid="save-checklist-btn"
                >
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t('save')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="daily" data-testid="daily-tab">
            {t('daily')} ({dailyChecklists.length})
          </TabsTrigger>
          <TabsTrigger value="monthly" data-testid="monthly-tab">
            {t('monthly')} ({monthlyChecklists.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="daily" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : dailyChecklists.length === 0 ? (
            <Card className="bg-white border border-slate-200">
              <CardContent className="py-12 text-center text-slate-500">
                {t('noData')}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {dailyChecklists.map((checklist) => (
                <ChecklistCard key={checklist.id} checklist={checklist} />
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="monthly" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : monthlyChecklists.length === 0 ? (
            <Card className="bg-white border border-slate-200">
              <CardContent className="py-12 text-center text-slate-500">
                {t('noData')}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {monthlyChecklists.map((checklist) => (
                <ChecklistCard key={checklist.id} checklist={checklist} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Checklists;
